import axios from 'axios';
import isEmpty from 'lodash.isempty';

// helpers
import AxiosServerInstanceClass from '../utilities/axiosServerInstance';
import { prepareSelectParam, prepareFilterParam } from '../utilities/queryParams';

// constants
import { API_URL_NEW, SSR_API_URL_NEW } from '../utilities/endpoints';
import { ALLOWED_QUERY_OPERATOR, TAXONOMIES } from '../utilities/constants';
import { MENA_COUNTRIES, PAKISTAN_COUNTRY_ID, TURKEY_COUNTRY_ID } from '../utilities/constants/countries';
import { EXCEPTION_NEWS_CATEGORIES, NEWS_POPULAR_CATEGORIES_IDS } from '../utilities/constants/newsCategories';


const taxonomyApi = {

    getTaxonomyType: (taxonomyType, maxLimit = 300) => {
        const taxoFields = prepareSelectParam(['id', 'name']);
        
        return new Promise((resolve, reject) => {
            axios.get(`${API_URL_NEW}/taxonomy-type/${taxonomyType}/taxonomy?fields=${taxoFields}&limit=${maxLimit}`).then(res => {
                resolve(res.data.data)
            }).catch(err => reject(err))
        })
    },

    getCountries: (maxLimit = 300) => {
        const taxoFields = prepareSelectParam(['id', 'name']);
        return new Promise((resolve, reject) => {
            const filters = prepareFilterParam([
                { key: 'is_region', value: [0], op: ALLOWED_QUERY_OPERATOR.equal }, // exclude regions
                { key: 'country', value: [0], op: ALLOWED_QUERY_OPERATOR.equal },   // exclude cities
            ]);
            const URL = `${API_URL_NEW}/location/?fields=${taxoFields}&filters=${filters}&limit=${maxLimit}&sort=name^:asc`;
            axios.get(URL, {}).then(response => {
                resolve(response.data.data);
            }).catch(err => reject(err))
        })
    },

    getUserRolesList: () => {
        const taxoFields = prepareSelectParam(['name', 'id']);
        return new Promise((resolve, reject) => {
            axios.get(`${API_URL_NEW}/taxonomy-type/${TAXONOMIES.userTypes}/taxonomy?fields=${taxoFields}`)
                .then(response => {
                    resolve(response.data.data)
                }).catch(err => reject(err))
        })
    },

    getIndustries: (maxLimit = 300) => {
        const taxoFields = prepareSelectParam(['id', 'name']);
        const filters = prepareFilterParam([
            { key: 'is_deleted', value: [0], op: ALLOWED_QUERY_OPERATOR.equal },
            { key: 'parent_id', value: [0], op: ALLOWED_QUERY_OPERATOR.equal },
        ]);
        return new Promise((resolve, reject) => {
            axios.get(`${API_URL_NEW}/industry?fields=${taxoFields}&filters=${filters}&limit=${maxLimit}&sort=name^:asc`)
                .then(response => {
                    resolve(response.data.data);
                }).catch(err => reject(err))
        })
    },

    getNewsTags: (maxLimit = 300) => {
        const taxoFields = prepareSelectParam(['id', 'name']);

        return new Promise((resolve, reject) => {
            axios.get(`${API_URL_NEW}/news-tag?fields=${taxoFields}&limit=${maxLimit}`)
                .then(response => {
                    resolve(response.data.data);
                }).catch(err => reject(err))
        })
    },

    getNewsPopularCategories: () => {
        const fields = prepareSelectParam(['id', 'name'])
        const filters = prepareFilterParam([
            { key: 'id', value: NEWS_POPULAR_CATEGORIES_IDS, op: ALLOWED_QUERY_OPERATOR.equal},
            { key: 'type', value: [14], op: ALLOWED_QUERY_OPERATOR.equal} // type 14 is news categories
        ]);

        const URL = `${API_URL_NEW}/setting/news-category?fields=${fields}&filters=${filters}`;

        return new Promise((resolve, reject) => {
            axios.get(URL)
                .then((response) => {
                    resolve(response.data.data)
                }).catch(err => reject(err))
        })
    },

    getNewsCategories: (maxLimit = 300) => {
        const fields = prepareSelectParam(['id', 'name']);

        const URL = `${API_URL_NEW}/news/setting/news-category?fields=${fields}&limit=${maxLimit}&sort=name^:asc`;

        return new Promise((resolve, reject) => {
            axios.get(URL)
                .then((response) => {
                    resolve(response.data.data)
                }).catch(err => reject(err))
        })
    },

    // get the list of MENATP countries for the SEO section on the directory
    getSEOCountries: () => {
        const fields = prepareSelectParam(['id', 'name', 'iso_code']);
        const filters = prepareFilterParam([{ key: 'id', value: [...MENA_COUNTRIES, PAKISTAN_COUNTRY_ID, TURKEY_COUNTRY_ID], op: ALLOWED_QUERY_OPERATOR.equal }])

        const url = `${API_URL_NEW}/location/?fields=${fields}&filters=${filters}&limit=200`;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then(response => {
                    if (response.data && !isEmpty(response.data.data)) {
                        resolve(response.data.data);
                    }
                }).catch(err => reject(err))
        })
    },

    // get the location object by an iso_code filter
    getLocationByISOCode: async (isoCode = "", token = "", refreshToken = "") => {
        const AxiosInstance = new AxiosServerInstanceClass(token, refreshToken);
        AxiosInstance.req();
        AxiosInstance.res();

        try {
            const fields = prepareSelectParam(['id', 'name', 'iso_code']);
            const filters = prepareFilterParam([{ key: 'iso_code', value: [isoCode], op: ALLOWED_QUERY_OPERATOR.equal }])
    
            const url = `${SSR_API_URL_NEW}/location?fields=${fields}&filters=${filters}`;
    
            const response = await AxiosInstance.axiosInstance.get(url);
            const data = response.data.data;
    
            return (data && data.length > 0) ? data[0] : {};
    
        } catch (error) {
            throw error;
        }
    },

    // get the news category object by the custom/temporary categoryCode
    getCategoryByCode: async (categoryCode = "", token = "", refreshToken = "") => {
        const AxiosInstance = new AxiosServerInstanceClass(token, refreshToken);
        AxiosInstance.req();
        AxiosInstance.res();

        try {
            let categoryName;

            // check if the categoryCode is one of the special codes
            if (EXCEPTION_NEWS_CATEGORIES[categoryCode]) {
                categoryName = EXCEPTION_NEWS_CATEGORIES[categoryCode];
            } else {
                categoryName = categoryCode.replace(/[-]/g, ' ');
            }

            const fields = prepareSelectParam(['id', 'name']);
            const filters = prepareFilterParam([{ key: 'name', value: [categoryName], op: ALLOWED_QUERY_OPERATOR.equal }])

            const url = `${SSR_API_URL_NEW}/news/setting/news-category?fields=${fields}&filters=${filters}`;

            const response = await AxiosInstance.axiosInstance.get(url);
            const data = response.data.data;

            return (data && data.length > 0) ? data[0] : {};

        } catch (error) {
            throw error;
        }
    },

    getResearchCategories: () => {
        const taxoFields = prepareSelectParam(['name', 'id']);
        const URL = `${API_URL_NEW}/research/setting/research-category?fields=${taxoFields}&sort=name^:asc`;
        return new Promise((resolve, reject) => {
            axios.get(URL).then(response => {
                resolve(response.data.data);
            }).catch(err => reject(err))
        })
    },
}

export default taxonomyApi
